import { randomId } from '@aha-app/mvc';
import type WhiteboardController from '../controllers/WhiteboardController';
import { zoomAndPanTo } from './zoom';
import type UndoEntry from '../collaboration/UndoEntry';
import { isComment } from '../objects/guards';
import type Comment from '../objects/Comment';
import { makeScriptRequest } from './requests';
import { type RecordId } from '../types';

// The AnnotationsManager is used to communicate with the annotations sidebar.
// The annotations sidebar is a reactive component that makes use of our
// existing Rails views. Here we're following the same pattern of using custom
// events to communicate between the sidebar and the whiteboard.

const annotationEvents = {
  saving: 'aha.whiteboard.annotation-saving',
  saved: 'aha.whiteboard.annotation-saved',
  destroyed: 'aha.whiteboard.annotation-destroyed',
  cancelled: 'aha.whiteboard.annotation-cancelled',
  resolvedStatusChange: 'aha.whiteboard.annotation-resolved-status-updated',
  commentFocus: 'aha.whiteboard.annotation-focused',
  showResolvedChange: 'aha.whiteboard.annotations-show-resolved-updated',
};

class AnnotationsManager {
  whiteboardId: string;
  commentsSidebarId: string;
  controller: WhiteboardController;
  // Used to check if comments were created in this whiteboard session
  annotationsManagerId: string;
  newAnnotationUndoEntry: UndoEntry | null = null;

  constructor({ whiteboardId, commentsSidebarId, controller }) {
    this.whiteboardId = whiteboardId;
    this.controller = controller;
    this.commentsSidebarId = commentsSidebarId;
    // Set a randomId that will persist for this session
    this.annotationsManagerId = randomId();

    document.addEventListener(
      annotationEvents.showResolvedChange,
      this.annotationsShowResolvedEventListener
    );

    document.addEventListener(
      annotationEvents.destroyed,
      this.annotationDestroyedEventListener
    );

    document.addEventListener(
      annotationEvents.commentFocus,
      this.annotationFocusedEventListener
    );

    document.addEventListener(
      annotationEvents.resolvedStatusChange,
      this.annotationResolvedStatusChangeEventListener
    );
  }

  getCommentObjects() {
    return this.controller.canvas.getObjects().filter(isComment);
  }

  getCommentById(objectId: string) {
    return this.getCommentObjects().find(obj => obj.id === objectId);
  }

  getCommentByAnnotationId(annotationId: RecordId) {
    return this.getCommentObjects().find(
      obj => obj.annotationId === annotationId
    );
  }

  annotationsShowResolvedEventListener = (event: CustomEvent): void => {
    if (event.detail?.showResolved) {
      this.controller.actionShowResolvedComments();
    } else {
      this.controller.actionHideResolvedComments();
    }
  };

  annotationFocusedEventListener = (event: CustomEvent): void => {
    if (event.detail?.annotationId) {
      this.focusCommentOnCanvas(event.detail.annotationId);
    }
  };

  focusCommentOnCanvas(
    annotationId: RecordId,
    animate = true,
    openSidepanel = false
  ): void {
    const comment = this.getCommentByAnnotationId(annotationId);
    if (!comment) return;
    this.controller.canvas.setActiveObject(comment);
    this.controller.canvas.requestRenderAll();
    // If the comment is attached to another object we should zoom to that
    // object, otherwise zoom straight to the comment
    const commentable = comment.commentable
      ? this.controller.canvas
          .getObjects()
          .filter(obj => obj.id === comment.commentable)
      : [];

    if (openSidepanel) {
      comment.focusInSidePanel(500);
    }

    const zoomObjs = commentable.length ? commentable : [comment];
    zoomAndPanTo(this.controller, zoomObjs, {
      animate,
      marginFactor: 0.6,
    });
  }

  annotationResolvedStatusChangeEventListener = (event: CustomEvent): void => {
    const comment = this.getCommentByAnnotationId(event.detail?.annotationId);
    if (comment) {
      comment.setResolved(event.detail?.resolved);
      this.controller.undoManager.completeUndoGroup();
    }
  };

  annotationDestroyedEventListener = (event: CustomEvent): void => {
    const comment = this.getCommentByAnnotationId(event.detail?.annotationId);
    if (comment) {
      comment.deleted(this.controller);
      comment.fire('deleted');
      this.controller.undoManager.completeUndoGroup();
    }
  };

  updateAnnotationSidebarFocusStatus(
    annotationId: RecordId | null, // if null, assume it's a new annotation
    focus: boolean
  ): void {
    const annotation = annotationId
      ? document.getElementById(`annotation-details-${annotationId}`)
      : document.querySelector<HTMLElement>('.new-annotation');
    if (!annotation) return;

    if (focus) {
      annotation
        .closest('.js-annotation-wrapper')
        ?.querySelectorAll('.annotation-details')
        .forEach(element => {
          element.classList.remove('annotation-details--focused');
        });

      annotation.classList.add('annotation-details--focused');

      // If selecting a collapsed comment in the whiteboard, expand it for
      // better visibility for the user
      if (annotation.querySelector('.annotation-comments--collapsed')) {
        annotation?.querySelector('.js-collapse-annotation')?.dispatchEvent(
          new MouseEvent('click', {
            view: window,
            bubbles: true,
            cancelable: true,
          })
        );
      }

      setTimeout(() => {
        const slideInner = annotation.closest<HTMLElement>(
          '.sidepanel-scroll-parent'
        );
        if (slideInner) {
          const top = annotation.offsetTop - slideInner.offsetTop;
          slideInner.scrollTo({ top });
        }
      }, 100);
    } else {
      annotation.classList.remove('annotation-details--focused');
    }
  }

  updateAnnotationHiddenStatus(annotationId: RecordId, hidden: boolean): void {
    const data = {
      annotation: {
        hidden,
      },
    };

    this.updateAnnotationRequest(annotationId, data)
      .then(() => {
        const sidebar = this.getSidebarElement();
        const annotation = sidebar?.querySelector(
          `#annotation-details-${annotationId} .js-annotation-state`
        );

        if (hidden) {
          annotation?.classList.add('annotation-details--hidden');
        } else {
          annotation?.classList.remove('annotation-details--hidden');
        }

        const visibleAnnotations = sidebar?.querySelectorAll(
          '.annotation-details .js-annotation-state:not(.annotation-details--hidden)'
        );
        if (visibleAnnotations?.length) {
          sidebar
            ?.querySelector('.annotation-empty-list')
            ?.classList.add('hide');
        } else {
          sidebar
            ?.querySelector('.annotation-empty-list')
            ?.classList.remove('hide');
        }
      })
      .catch(() => {
        console.error('Failed update annotation request');
      });
  }

  annotationSavingEventListener = (event: CustomEvent): void => {
    const comment = this.getCommentByAnnotationId(event.detail?.annotationId);
    if (comment) {
      comment.set('isSaving', true);
      this.controller.canvas.requestRenderAll();
    }
  };

  annotationSavedEventListener = (event: CustomEvent): void => {
    const comment = this.getCommentById(event.detail?.objectId);
    if (comment) {
      // We set the annotationId and commentNumber on the comment object
      // without adding an undo entry as it can cause the undo stack to get
      // into an undesirable state. The object on the original undo entry
      // will be mutated, adding these properties anyway, which is actually
      // what we want.
      this.controller.undoManager.withoutUndo(() => {
        comment.setAnnotation(event.detail.annotationId, event.detail.position);
      });
      this.controller.undoManager.completeUndoGroup();
      this.controller.actionCommentComplete();
    }
  };

  annotationCancelledEventListener = (event): void => {
    const commentObj = this.getCommentById(event.detail?.objectId);
    this.cancelAnnotation(commentObj);
  };

  getSidebarElement(): Element | null {
    return document.querySelector(
      `.whiteboard-annotations__sidebar[data-annotations-sidebar-id="sidebar-${this.commentsSidebarId}"]`
    );
  }

  updateAnnotationResolvedStatus(
    annotationId: RecordId,
    resolved: boolean
  ): void {
    const data = {
      annotation: {
        resolved,
      },
    };

    this.updateAnnotationRequest(annotationId, data)
      .then(() => {
        const sidebar = this.getSidebarElement();
        const annotation = document.getElementById(
          `annotation-details-${annotationId}`
        );

        const elements = [
          {
            element: annotation?.querySelector('.js-annotation-state'),
            className: 'annotation-resolved',
          },
          {
            element: annotation,
            className: 'annotation-details--resolved-hidden',
          },
          {
            element: sidebar?.querySelector(
              `.annotation-marker[data-annotation-id="${annotationId}"]`
            ),
            className: 'annotation-marker--resolved',
          },
        ];

        elements.forEach(({ element, className }) => {
          if (resolved) {
            element?.classList.add(className);
          } else {
            element?.classList.remove(className);
          }
        });

        const isCollapsed =
          annotation?.querySelectorAll('.annotation-comments--collapsed')
            ?.length !== 0;

        // If resolved, should be collapsed. If unresolved, should be expanded.
        if (isCollapsed !== resolved) {
          annotation?.querySelector('.js-collapse-annotation')?.dispatchEvent(
            new MouseEvent('click', {
              view: window,
              bubbles: true,
              cancelable: true,
            })
          );
        }
      })
      .catch(() => {
        console.error('Failed update annotation request');
      });
  }

  removeNewAnnotationListeners(): void {
    document.removeEventListener(
      annotationEvents.cancelled,
      this.annotationCancelledEventListener
    );
    document.removeEventListener(
      annotationEvents.saving,
      this.annotationSavingEventListener
    );
    document.removeEventListener(
      annotationEvents.saved,
      this.annotationSavedEventListener
    );
  }

  newAnnotation(commentObj: Comment): void {
    document.addEventListener(
      annotationEvents.cancelled,
      this.annotationCancelledEventListener
    );

    document.addEventListener(
      annotationEvents.saving,
      this.annotationSavingEventListener
    );

    document.addEventListener(
      annotationEvents.saved,
      this.annotationSavedEventListener
    );

    const data = {
      whiteboard_object_id: commentObj.id,
      'annotation[annotatable_type]': 'Whiteboard',
      'annotation[annotatable_id]': this.whiteboardId,
      type: 'Annotation::WhiteboardObject',
    };

    void this.newAnnotationRequest(data)
      .catch(() => {
        this.cancelAnnotation(commentObj);
        console.error('Failed new annotation request');
      })
      .then(() => {
        this.updateAnnotationSidebarFocusStatus(null, true);
      });
  }

  cancelAnnotation(commentObj?: Comment): void {
    // If the comment is saving, it's too late to cancel. It's very unlikely
    // that this could happen, but it is possible.
    if (commentObj?.isSaving) return;
    this.removeNewAnnotationListeners();

    if (commentObj && !commentObj.annotationId) {
      if (this.newAnnotationUndoEntry) {
        this.controller.undoManager.completeUndoGroup();
        this.controller.undoManager.removeUndo(this.newAnnotationUndoEntry);
        this.newAnnotationUndoEntry = null;
      }

      commentObj.deleted(this.controller);
      this.controller.canvas.remove(commentObj);
      const deleteEntry = this.controller.undoManager.completeUndoGroup()!;
      this.controller.undoManager.removeUndo(deleteEntry);
    }

    this.controller.actionCommentComplete();

    const sidebar = this.getSidebarElement();
    const annotation = sidebar?.querySelector('.new-annotation');

    if (!annotation) return;

    const annotations =
      annotation
        ?.closest('.js-comment-list-full')
        ?.querySelectorAll(':scope .annotation-details:not(.new-annotation)') ??
      [];

    let hasAnnotations = false;

    for (const annotation of annotations) {
      if (
        annotation?.querySelector(
          '.js-annotation-state:not(.annotation-details--hidden)'
        )
      ) {
        hasAnnotations = true;
        break;
      }
    }

    // Show the add message if this is the last annotation
    if (!hasAnnotations) {
      sidebar?.querySelector('.js-comment-list-full')?.classList.add('hide');
      sidebar?.querySelector('.js-comment-list-add')?.classList.remove('hide');
    }

    annotation.remove();
  }

  destroy(): void {
    document.removeEventListener(
      annotationEvents.showResolvedChange,
      this.annotationsShowResolvedEventListener
    );

    document.removeEventListener(
      annotationEvents.destroyed,
      this.annotationDestroyedEventListener
    );

    document.removeEventListener(
      annotationEvents.commentFocus,
      this.annotationFocusedEventListener
    );

    document.removeEventListener(
      annotationEvents.resolvedStatusChange,
      this.annotationResolvedStatusChangeEventListener
    );

    this.removeNewAnnotationListeners();
  }

  get requestBaseUrl() {
    return this.controller.props.sharingOptions?.collaborationBaseURL ?? '';
  }

  get requestHeaders() {
    const { sharingOptions } = this.controller.props;
    const authorization = sharingOptions?.getSharingToken?.();
    if (authorization) {
      return { Authorization: `Bearer ${authorization}` };
    }
  }

  async newAnnotationRequest(data: Record<string, string>): Promise<void> {
    await makeScriptRequest(
      `${this.requestBaseUrl}/annotations/new?${new URLSearchParams(
        data
      ).toString()}`,
      { headers: this.requestHeaders }
    );
  }

  async updateAnnotationRequest(
    annotationId: RecordId,
    data: Record<string, unknown>
  ): Promise<void> {
    const res = await fetch(
      `${this.requestBaseUrl}/annotations/${annotationId}`,
      {
        body: JSON.stringify(data),
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': window.csrfToken(),
          ...this.requestHeaders,
        },
      }
    );
    if (!res.ok) {
      throw new Error();
    }
  }
}

export default AnnotationsManager;
